import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import {
  AnonymousBoolFlag,
  AuthenticatedStringFlagKey,
  BoolFlag,
  CreateDefaultAuthenticatedStringFlagConfig,
  CreateDefaultBoolFlagConfig,
  FlagConverter,
  Flags,
  PartialFlags,
} from 'routes/profile/models/Flags';
import { DataType } from 'rpc/model/squareup/feature/relay/common/data_type';
import { GetFlagRequest } from 'rpc/model/squareup/feature/relay/flags/message/get_flags';
import { fetchFlags } from 'services/flags';
import { AppState } from 'store';
import { getOrSetSavt } from 'utils/cookies';
import { Token } from '../rpc/model/squareup/feature/relay/common/token';

export type FeatureState = {
  flags: Flags;
  areAnonymousFlagsLoaded: boolean;
};

export const initialState: FeatureState = {
  areAnonymousFlagsLoaded: false,
  flags: {
    boolean: CreateDefaultBoolFlagConfig(),
    string: CreateDefaultAuthenticatedStringFlagConfig(),
  },
};

export const selectBoolFlag = createSelector(
  (state: AppState) => state.feature.flags,
  (_: AppState, flag: BoolFlag | AnonymousBoolFlag) => flag,
  (flags: Flags, flag: BoolFlag | AnonymousBoolFlag) => flags.boolean[flag]
);

export const selectStringFlag = createSelector(
  (state: AppState) => state.feature.flags,
  (_: AppState, flag: AuthenticatedStringFlagKey) => flag,
  (flags: Flags, flag: AuthenticatedStringFlagKey) => flags.string[flag]
);

export const fetchFeatures = createAsyncThunk<
  PartialFlags,
  { personToken: string; userEmail?: string; country?: string }
>('feature/fetchFeatures', async ({ personToken, userEmail, country }) => {
  const booleanFlagRequests = Object.values(BoolFlag).map((flag) => {
    return new GetFlagRequest.Builder()
      .flagName(flag)
      .flagType(DataType.BOOLEAN)
      .build();
  });

  const stringFlagRequests = Object.values(AuthenticatedStringFlagKey).map(
    (flag) => {
      // For the referrals experiment, we need to use the `userEmail` as the user token
      // since LaunchDarkly does not support attribute matching for Big Segments
      if (
        flag === AuthenticatedStringFlagKey.referralsExperimentVariant &&
        userEmail
      ) {
        return new GetFlagRequest.Builder()
          .flagName(flag)
          .flagType(DataType.STRING)
          .userToken(
            Token.create({
              token: userEmail,
              type: Token.Type.UNKNOWN,
            })
          )
          .build();
      }

      return new GetFlagRequest.Builder()
        .flagName(flag)
        .flagType(DataType.STRING)
        .build();
    }
  );

  const flags = await fetchFlags(
    [...booleanFlagRequests, ...stringFlagRequests],
    personToken,
    userEmail,
    country
  );

  return FlagConverter.fromRpcGetFlagsResponse(flags);
});

export const fetchAnonymousFeatures = createAsyncThunk<PartialFlags>(
  'feature/fetchAnonymousFeatures',
  async () => {
    const savt = getOrSetSavt();
    const flags = await fetchFlags(
      Object.values(AnonymousBoolFlag).map((flag) => {
        return new GetFlagRequest.Builder()
          .flagName(flag)
          .flagType(DataType.BOOLEAN)
          .build();
      }),
      savt
    );

    return FlagConverter.fromRpcGetFlagsResponse(flags);
  }
);

export const fetchAnonymousFeaturesNoStore = async () => {
  const savt = getOrSetSavt();
  const flags = await fetchFlags(
    Object.values(AnonymousBoolFlag).map((flag) => {
      return new GetFlagRequest.Builder()
        .flagName(flag)
        .flagType(DataType.BOOLEAN)
        .build();
    }),
    savt
  );

  return FlagConverter.fromRpcGetFlagsResponse(flags);
};

const featureSlice = createSlice({
  name: 'feature',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchFeatures.fulfilled, (state, action) => {
      state.flags.boolean = {
        ...state.flags.boolean,
        ...action.payload.boolean,
      };
      state.flags.string = {
        ...state.flags.string,
        ...action.payload.string,
      };
    });

    builder.addCase(fetchAnonymousFeatures.fulfilled, (state, action) => {
      state.areAnonymousFlagsLoaded = true;
      state.flags.boolean = {
        ...state.flags.boolean,
        ...action.payload.boolean,
      };
    });

    builder.addCase(fetchAnonymousFeatures.rejected, (state) => {
      state.areAnonymousFlagsLoaded = true;
    });
  },
});

export default featureSlice.reducer;
